import * as Dialog from '@radix-ui/react-dialog';
import axios from 'axios';
import { X } from 'lucide-react';
import { useState } from 'react';
import type { PortfolioMandate } from 'shared';
import { toast } from 'sonner';

import { useUpdateAccount } from '../../features/accounts/mutations';
import { MANDATE_OPTIONS, mandateLabel } from '../../lib/accountMandate';
import type { AccountDto } from '../../lib/api/endpoints';
import { Button } from '../ui/Button';
import { Label } from '../ui/Label';

interface MandateImpact {
  executionCount: number;
  importCount: number;
  positionRecordCount: number;
  tradePlanCount: number;
  investmentThesisCount: number;
}

interface PendingMandateChange {
  requestedMandate: PortfolioMandate;
  impact: MandateImpact;
}

interface ApiErrorBody {
  error?: {
    code?: string;
    message?: string;
    details?: unknown;
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function impactFrom(value: unknown): MandateImpact | null {
  if (!isRecord(value)) return null;
  const executionCount = value.executionCount;
  const importCount = value.importCount;
  const positionRecordCount = value.positionRecordCount;
  const tradePlanCount = value.tradePlanCount;
  const investmentThesisCount = value.investmentThesisCount ?? 0;
  if (
    typeof executionCount !== 'number' ||
    typeof importCount !== 'number' ||
    typeof positionRecordCount !== 'number' ||
    typeof tradePlanCount !== 'number' ||
    typeof investmentThesisCount !== 'number'
  ) {
    return null;
  }
  return {
    executionCount,
    importCount,
    positionRecordCount,
    tradePlanCount,
    investmentThesisCount,
  };
}

function confirmationFrom(
  error: unknown,
  requestedMandate: PortfolioMandate,
): PendingMandateChange | null {
  if (!axios.isAxiosError<ApiErrorBody>(error)) return null;
  if (error.response?.status !== 409) return null;
  if (error.response.data.error?.code !== 'MANDATE_CHANGE_CONFIRMATION_REQUIRED') return null;
  const details = error.response.data.error.details;
  if (!isRecord(details)) return null;
  const impact = impactFrom(details.impact);
  return impact ? { requestedMandate, impact } : null;
}

function errorMessage(error: unknown): string {
  if (axios.isAxiosError<ApiErrorBody>(error)) {
    return error.response?.data.error?.message ?? 'Could not update account mandate.';
  }
  return 'Could not update account mandate.';
}

export function AccountMandateControl({ account }: { account: AccountDto }) {
  const updateAccount = useUpdateAccount();
  const [pendingChange, setPendingChange] = useState<PendingMandateChange | null>(null);
  const [error, setError] = useState<string | null>(null);

  function requestChange(requestedMandate: PortfolioMandate) {
    if (requestedMandate === account.mandate) return;
    setError(null);
    updateAccount.mutate(
      { id: account.id, input: { mandate: requestedMandate } },
      {
        onSuccess: () => toast.success('Account mandate updated.'),
        onError: (mutationError) => {
          const confirmation = confirmationFrom(mutationError, requestedMandate);
          if (confirmation) {
            setPendingChange(confirmation);
            return;
          }
          setError(errorMessage(mutationError));
        },
      },
    );
  }

  function confirmChange() {
    if (!pendingChange) return;
    setError(null);
    updateAccount.mutate(
      {
        id: account.id,
        input: {
          mandate: pendingChange.requestedMandate,
          confirmMandateChange: true,
        },
      },
      {
        onSuccess: () => {
          setPendingChange(null);
          toast.success('Account mandate updated. Historical data was preserved.');
        },
        onError: (mutationError) => setError(errorMessage(mutationError)),
      },
    );
  }

  const selectedOption = MANDATE_OPTIONS.find((option) => option.value === account.mandate);

  return (
    <div className="mt-3 max-w-xl space-y-2">
      <Label htmlFor={`mandate-${account.id}`}>Mandate</Label>
      <select
        id={`mandate-${account.id}`}
        className="h-10 w-full rounded border border-border bg-bg px-3 text-sm text-text"
        value={account.mandate}
        disabled={updateAccount.isPending}
        onChange={(event) => {
          const selected = MANDATE_OPTIONS.find((option) => option.value === event.target.value);
          if (selected) requestChange(selected.value);
        }}
      >
        {MANDATE_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <p className="text-xs leading-5 text-text-muted">{selectedOption?.description}</p>
      {error && !pendingChange ? (
        <p className="text-sm text-danger" role="alert">
          {error}
        </p>
      ) : null}

      <Dialog.Root
        open={pendingChange !== null}
        onOpenChange={(open) => {
          if (!open && !updateAccount.isPending) {
            setPendingChange(null);
            setError(null);
          }
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-black/60" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded border border-border bg-surface p-5 text-text shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <Dialog.Title className="text-lg font-semibold">
                  Change account mandate?
                </Dialog.Title>
                <Dialog.Description className="mt-1 text-sm text-text-muted">
                  {account.name}: {mandateLabel(account.mandate)} to{' '}
                  {pendingChange ? mandateLabel(pendingChange.requestedMandate) : ''}
                </Dialog.Description>
              </div>
              <Dialog.Close asChild>
                <Button
                  aria-label="Close mandate confirmation"
                  size="sm"
                  type="button"
                  variant="ghost"
                  disabled={updateAccount.isPending}
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </Button>
              </Dialog.Close>
            </div>

            {pendingChange ? (
              <div className="mt-5 space-y-4 text-sm">
                <p>
                  Existing positions, executions, imports, and TradePlans will be preserved.
                  Historical TradePlans remain visible, but active TradePlan actions are available
                  only while this account has the Swing Trading mandate.
                </p>
                <dl className="grid grid-cols-2 gap-3 rounded border border-border bg-bg p-3 tabular-nums">
                  <div>
                    <dt className="text-text-muted">Position records</dt>
                    <dd className="font-semibold">{pendingChange.impact.positionRecordCount}</dd>
                  </div>
                  <div>
                    <dt className="text-text-muted">Executions</dt>
                    <dd className="font-semibold">{pendingChange.impact.executionCount}</dd>
                  </div>
                  <div>
                    <dt className="text-text-muted">Imports</dt>
                    <dd className="font-semibold">{pendingChange.impact.importCount}</dd>
                  </div>
                  <div>
                    <dt className="text-text-muted">TradePlans</dt>
                    <dd className="font-semibold">{pendingChange.impact.tradePlanCount}</dd>
                  </div>
                  <div>
                    <dt className="text-text-muted">Investment theses</dt>
                    <dd className="font-semibold">{pendingChange.impact.investmentThesisCount}</dd>
                  </div>
                </dl>
                {error ? (
                  <p className="text-danger" role="alert">
                    {error}
                  </p>
                ) : null}
                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <Dialog.Close asChild>
                    <Button type="button" variant="secondary" disabled={updateAccount.isPending}>
                      Cancel
                    </Button>
                  </Dialog.Close>
                  <Button type="button" disabled={updateAccount.isPending} onClick={confirmChange}>
                    {updateAccount.isPending ? 'Changing...' : 'Confirm mandate change'}
                  </Button>
                </div>
              </div>
            ) : null}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
